import React, { useEffect } from 'react';
import { TeamMember, TravelerTags } from '@marageti/z4-sdk/lib/people';
import { ReferralType } from '@marageti/z4-sdk/lib/travel/TripRequest';
import {
  CopyButton,
  DetailCard,
  DetailCardProps,
  formatCountryCodesToString,
  getFormattedDateShortMonthDDYYYY,
  useApiClient,
  useFetchImmutable,
  TripRequestChips,
  OtherChips,
  TravelerTagChips,
  OutlinedIcons,
  getBudgetPerPersonString,
  getTotalBudgetString,
  getPPPDString,
  getBudgetFlexibilityString,
} from '@marageti/z4-lib';
import { TripRequest, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import {
  Box, Divider, Icon, IconButton, Link, Stack, Tooltip, Typography,
} from '@mui/material';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';

const {
  OutlinedInformationCircleIcon, OutlinedExternalLinkIcon, OutlinedQuestionMarkCircleIcon, OutlinedShieldCheckIcon, OutlinedShieldExclamationIcon,
} = OutlinedIcons;
const { ChipPRL, ChipRepeat } = OtherChips;
const { getTripRequestStatusChip } = TripRequestChips;
const { getTravelerTagChip } = TravelerTagChips;

// We will use this endpoint to fetch the itinerary canonical path for the time being
// This will be refactored and be provided by the Traveler Service at a future date
// This is a public route
const z3ApiEnpoint: { [key: string]: string } = {
  dev: 'https://api-staging.zicasso.com',
  prod: 'https://api.zicasso.com',
  qa: 'https://api-qa.zicasso.com',
  staging: 'https://api-staging.zicasso.com',
  local: 'http://api-staging.zicasso.com',
};
const z3Enpoint: { [key: string]: string } = {
  dev: 'https://www-staging.zicasso.com',
  prod: 'https://www.zicasso.com',
  qa: 'https://www-qa.zicasso.com',
  staging: 'https://www-staging.zicasso.com',
  local: 'http://www-staging.zicasso.com',
};

type TripRequestProps = {
  chips?: string[];
  title?: string;
  titleTypographyProps?: DetailCardProps['titleTypographyProps']
  tripRequest: Omit<TripRequest, 'traveler'>;
  action?: DetailCardProps['action'];
  displayExternalLink?: boolean;
  loggedInTeamMember: TeamMember | null;
};

const ChipList = ({
  chips = undefined,
}: {
  chips?: string[],
}) => {
  if (!chips || chips.length === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
    >
      {chips.map((chip: string) => {
        switch (chip) {
          case TripRequestStatus.PendingStatus:
          case TripRequestStatus.VerifiedStatus:
          case TripRequestStatus.PostponedStatus:
          case TripRequestStatus.MatchedStatus:
          case TripRequestStatus.SoldStatus:
          case TripRequestStatus.CanceledStatus:
          case TripRequestStatus.JunkStatus:
          case TripRequestStatus.ReferredStatus:
            return getTripRequestStatusChip(chip, 'small');
          case TravelerTags.VIP:
          case TravelerTags.BAD_APPLE:
          case TravelerTags.RED_FLAG:
          case TravelerTags.TRAVEL_AGENT:
          case TravelerTags.JUNK:
          case TravelerTags.FOBY:
            return getTravelerTagChip(chip, 'small');
          case ReferralType.OneTimePartnerReferral:
          case ReferralType.LifetimePartnerReferral:
            return <ChipPRL key={chip} />;
          case 'REPEAT_TRAVELER':
            return <ChipRepeat key={chip} />;
          default:
            return null;
        }
      })}
    </Stack>
  );
};

const TripRequestCardFooter = ({
  countryCodes,
  submittedDate,
  submittedTime,
  submittedFrom,
  recaptchaScore,
  updatedAt,
}: {
  countryCodes: CountryCodeMap;
  submittedDate: string;
  submittedTime: string;
  submittedFrom: string;
  recaptchaScore: number | null;
  updatedAt: string;
}) => {
  const submittedOn = getFormattedDateShortMonthDDYYYY(submittedDate);
  const lastUpdatedOn = getFormattedDateShortMonthDDYYYY(updatedAt);
  const submittedTimeText = submittedTime ? `at ${submittedTime} ` : '';
  const submittedFromText = submittedFrom ? `from an IP address in ${countryCodes[submittedFrom]}. ` : '';

  const recaptchaScoreMap = [{
    min: 0.9,
    icon: <OutlinedShieldCheckIcon fontSize="small" color="success" sx={{ height: '24px', width: '24px' }} />,
    description: 'ReCaptcha score of 0.9 indicates it was submitted by a human. ',
  },
  {
    min: 0.7,
    icon: <OutlinedShieldCheckIcon fontSize="small" color="success" sx={{ height: '24px', width: '24px' }} />,
    description: 'ReCaptcha score of 0.7 indicates it was likely submitted by a human. ',
  },
  {
    min: 0.5,
    icon: <OutlinedShieldExclamationIcon fontSize="small" color="error" sx={{ height: '24px', width: '24px' }} />,
    description: 'ReCaptcha score of 0.5 indicates it was probably submitted by a bot. ',
  },
  {
    min: 0.3,
    icon: <OutlinedShieldExclamationIcon fontSize="small" color="error" sx={{ height: '24px', width: '24px' }} />,
    description: 'ReCaptcha score of 0.3 indicates it was likely submitted by a bot. ',
  },
  {
    min: 0,
    icon: <OutlinedShieldExclamationIcon fontSize="small" color="error" sx={{ height: '24px', width: '24px' }} />,
    description: 'ReCaptcha score of 0.1 indicates it was submitted by a bot. ',
  },
  ];

  const undefinedScore = {
    icon: <OutlinedQuestionMarkCircleIcon fontSize="small" color="action" sx={{ height: '24px', width: '24px' }} />,
    description: 'ReCaptcha score: not available. ',
  };

  const recaptchaNotes = typeof recaptchaScore === 'number' ? recaptchaScoreMap.find((score) => recaptchaScore >= score.min) : undefinedScore;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Icon>{recaptchaNotes?.icon}</Icon>
      <Box>
        <Typography variant="caption" color="text.secondary" component="span" style={{ fontStyle: 'italic' }}>
          Submitted on
          {' '}
          {submittedOn}
          {' '}
          {submittedTimeText}
          {submittedFromText}
          {recaptchaNotes?.description}
          Last updated on
          {' '}
          {lastUpdatedOn}
          .
        </Typography>
      </Box>
    </Stack>
  );
};

const TripRequestCard = ({
  action = false,
  chips = undefined,
  title = undefined,
  titleTypographyProps = {},
  tripRequest,
  displayExternalLink = false,
  loggedInTeamMember,
}: TripRequestProps) => {
  const apiClient = useApiClient();
  const countryCodes = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const [itineraryPath, setItineraryPath] = React.useState<string | undefined>(undefined);

  const {
    adultAgeGroups,
    basedOnItineraryId,
    budgetFlexibility,
    childAgeGroups,
    countries,
    departureDate,
    numberOfAdults,
    numberOfChildren,
    pppd,
    referenceId,
    totalBudget,
    totalNights,
    tripDescription,
    createdAt,
    updatedAt,
    submittedFrom,
    recaptchaScore,
  } = tripRequest;

  useEffect(() => {
    if (basedOnItineraryId) {
      const fetchItineraryPath = async () => {
        const response = await fetch(`${z3ApiEnpoint[process.env.REACT_APP_STAGE || 'dev']}/v2/paths?entity=${basedOnItineraryId}`);
        const data = await response.json();
        const path = data.find((p: { [key: string]: any }) => p.redirect === false).canonicalPath;
        setItineraryPath(`${z3Enpoint[process.env.REACT_APP_STAGE || 'dev']}/${path}`);
      };

      fetchItineraryPath();
    }
  }, [basedOnItineraryId]);

  if (!countryCodes.data) {
    return null;
  }

  const countriesString = countryCodes.data && formatCountryCodesToString(countries, countryCodes.data);
  const whenString = `${getFormattedDateShortMonthDDYYYY(departureDate) || ''} · ${totalNights} Nights`;

  let travelersString = `${numberOfAdults} Adults ${adultAgeGroups?.map((group) => `(${group})`).join(' ')}`;
  if (numberOfChildren > 0) {
    travelersString += ` · ${numberOfChildren} Children ${childAgeGroups?.map((group) => `(${group})`).join(' ')}`;
  }

  const basedOnItineraryContent = basedOnItineraryId && (
    <Tooltip title={itineraryPath}>
      <span>
        <Link href={itineraryPath} target="_blank" rel="noopener noreferrer">Link to Itinerary</Link>
      </span>
    </Tooltip>
  );
  const referenceIdContent = referenceId && (
    <>
      {referenceId}
      <CopyButton textToCopy={referenceId} iconFontSize="small" size="small" />
    </>
  );

  const timeZoneName = loggedInTeamMember?.timeZone?.name || '';
  const submittedTime = timeZoneName ? new Date(createdAt).toLocaleTimeString('en-US', {
    timeZone: timeZoneName.replace(' ', '_'),
    timeStyle: 'short',
  }) : '';

  return (
    <DetailCard
      action={(
        <Stack direction="row" spacing={1}>
          {action}
          {displayExternalLink && (
          <IconButton component={Link} aria-label="Go to trip request" href={`/trip-requests/${tripRequest.id}/matches`} target="_blank">
            <OutlinedExternalLinkIcon color="primary" />
          </IconButton>
          )}
        </Stack>
      )}
      chips={chips ? <ChipList chips={chips} /> : undefined}
      title={(
        <Box display="flex" gap="8px">
          {title}
        </Box>
      )}
      titleTypographyProps={titleTypographyProps}
      items={[
        {
          title: (
            <Box display="flex">
              Description
              <Tooltip title={`${tripDescription.length} characters`} placement="top">
                <span>
                  <OutlinedInformationCircleIcon
                    color="action"
                    sx={{
                      ml: 0.5,
                      fontSize: 16,
                    }}
                  />
                </span>
              </Tooltip>
            </Box>
          ),
          content: tripDescription,
        },
        { title: 'Countries', content: countriesString },
        { title: 'When', content: whenString },
        { title: 'Budget Per Person', content: `${getBudgetPerPersonString(totalBudget, numberOfAdults, numberOfChildren)} · ${getPPPDString(pppd)}` },
        { title: 'Total Budget', content: `${getTotalBudgetString(totalBudget)}${budgetFlexibility ? ` · ${getBudgetFlexibilityString(budgetFlexibility)}` : ''}` },
        { title: 'Travelers', content: travelersString },
        { title: 'Based on Itinerary', content: basedOnItineraryContent },
        { title: 'Reference ID', content: referenceIdContent },
        <Divider key="trip-request-card-divider" />,
        <TripRequestCardFooter
          key="trip-request-card-footer"
          countryCodes={countryCodes.data}
          submittedDate={createdAt}
          submittedTime={submittedTime}
          submittedFrom={submittedFrom}
          recaptchaScore={recaptchaScore}
          updatedAt={updatedAt}
        />,
      ]}
    />
  );
};

export default TripRequestCard;
